import { useEffect, useState } from "react";
import { GraduationCap, Plus, Trash2, Play, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatDateBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function extractEmbed(url) {
  if (!url) return null;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // YouTube already embed
  if (url.includes("youtube.com/embed")) return url;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url; // assume direct
}

export default function Training() {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", title: "", description: "", video_url: "", duration_minutes: "" });

  const isDiretora = user.role === ROLES.DIRETORA;

  const load = async () => {
    const { data } = await api.get("/training");
    setVideos(data);
  };

  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);

  const create = async () => {
    if (!form.product_id || !form.title || !form.video_url) {
      return toast.error("Preencha produto, título e URL do vídeo.");
    }
    try {
      await api.post("/training", {
        ...form,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      });
      toast.success("Treinamento cadastrado.");
      setOpen(false);
      setForm({ product_id: "", title: "", description: "", video_url: "", duration_minutes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este treinamento?")) return;
    try {
      await api.delete(`/training/${id}`);
      load();
    } catch {}
  };

  const toggleWatched = async (v) => {
    try {
      if (v.watched_by_me) await api.post(`/training/${v.id}/unwatched`);
      else await api.post(`/training/${v.id}/watched`);
      load();
    } catch {}
  };

  if (!videos) return <Skeleton className="h-96" />;

  const filtered = filter === "all" ? videos : videos.filter(v => v.product_id === filter);
  const watched = videos.filter(v => v.watched_by_me).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Treinamentos</h1>
          <p className="text-sm text-slate-600 mt-1">
            {isDiretora
              ? "Cadastre vídeos de capacitação por produto. Os consultores acessam os treinamentos relevantes."
              : `Portal interno de capacitação. ${watched} de ${videos.length} vídeos assistidos.`}
          </p>
        </div>
        {isDiretora && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-midnight hover:bg-ink text-white" data-testid="new-training-button">
                <Plus className="w-4 h-4 mr-2" /> Novo treinamento
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Cadastrar vídeo de treinamento</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Produto</Label>
                  <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Título</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label>URL do vídeo (YouTube ou Vimeo)</Label>
                  <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..." />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label>Duração (min)</Label>
                  <Input type="number" value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={create} className="bg-midnight hover:bg-ink text-white" data-testid="save-training-button">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-xs">Filtrar por produto:</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-72"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os produtos</SelectItem>
            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {active && (
        <Card className="card-soft">
          <CardHeader className="flex flex-row justify-between items-start">
            <div>
              <CardTitle className="text-base">{active.title}</CardTitle>
              <div className="text-xs text-slate-500 mt-1">{active.product_name}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setActive(null)}>Fechar player</Button>
          </CardHeader>
          <CardContent>
            <div className="aspect-video bg-black rounded-md overflow-hidden">
              <iframe
                src={extractEmbed(active.video_url)}
                title={active.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {active.description && (
              <p className="mt-3 text-sm text-slate-700">{active.description}</p>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button onClick={() => toggleWatched(active)}
                className={active.watched_by_me ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-midnight hover:bg-ink text-white"}
                data-testid="mark-watched-button">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {active.watched_by_me ? "Assistido" : "Marcar como assistido"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <Card className="card-soft sm:col-span-2 lg:col-span-3">
            <CardContent className="py-10 text-sm text-slate-500 text-center">
              Nenhum vídeo cadastrado para este filtro.
            </CardContent>
          </Card>
        ) : filtered.map(v => (
          <Card key={v.id} className="card-soft hover:card-elevated transition-shadow cursor-pointer"
            onClick={() => setActive(v)} data-testid={`training-card-${v.id}`}>
            <CardContent className="p-4">
              <div className="aspect-video bg-slate-100 rounded-md flex items-center justify-center mb-3">
                <Play className="w-10 h-10 text-midnight" />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-slate-900 truncate">{v.title}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">{v.product_name}</div>
                  {v.duration_minutes && (
                    <div className="text-xs text-slate-500 mt-0.5">{v.duration_minutes} min</div>
                  )}
                </div>
                {v.watched_by_me && (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                )}
              </div>
              {isDiretora && (
                <div className="mt-2 flex justify-end">
                  <button onClick={(e) => { e.stopPropagation(); remove(v.id); }}
                    className="text-xs text-red-600 hover:underline">
                    <Trash2 className="inline w-3 h-3 mr-1" /> Remover
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
